import { SessionStorageKeys } from '../../constants';
import { QueryParamKeys } from '../../constants/query-params';

// Metadata query params are the params we capture on landing and persist so they survive a refresh.
// Storage is sessionStorage, not localStorage: these params describe how *this* visit began, so they
// must not outlive the tab and attach themselves to an unrelated application started later on the
// same computer. `entrypoint` is additionally stripped from the URL; the marketing params are
// deliberately left in place because Segment and FullStory read campaign data off the live URL.
export interface MetadataParams {
  entrypoint?: string;
  utmId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  adPlatform?: string;
  adClickId?: string;
}

type MarketingField = Exclude<keyof MetadataParams, 'entrypoint'>;

const UTM_PARAMS: [QueryParamKeys, MarketingField][] = [
  [QueryParamKeys.UtmId, 'utmId'],
  [QueryParamKeys.UtmSource, 'utmSource'],
  [QueryParamKeys.UtmMedium, 'utmMedium'],
  [QueryParamKeys.UtmCampaign, 'utmCampaign'],
  [QueryParamKeys.UtmTerm, 'utmTerm'],
  [QueryParamKeys.UtmContent, 'utmContent'],
];

// Each advertiser stamps its own click-id param on the landing URL. First match wins — only one
// adPlatform/adClickId pair is ever recorded.
const AD_PLATFORMS: [QueryParamKeys, string][] = [
  [QueryParamKeys.Gclid, 'google'], // Google Ads, including YouTube
  [QueryParamKeys.Fbclid, 'meta'],
  [QueryParamKeys.Msclkid, 'microsoft'],
  [QueryParamKeys.LiFatId, 'linkedin'],
  [QueryParamKeys.Ttclid, 'tiktok'],
];

const STORAGE_KEY = SessionStorageKeys.MetadataParams;

const readFromStorage = (): MetadataParams => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    console.warn('Failed to read metadata params from sessionStorage', err);
    return {};
  }
};

// The marketing params describe a single visit, so the URL is always the source of truth for the
// whole bundle: we take all of them from the URL, or none. A URL with no marketing params wipes what
// was stored, so an earlier campaign can never attach itself to a later, unrelated application — and
// a URL with only utm_source can never be blended into an earlier visit's campaign. Unlike
// `entrypoint` these params are left in the URL, so a refresh re-reads them and loses nothing.
const resolveMarketingParams = (params: URLSearchParams): MetadataParams => {
  const marketing: MetadataParams = {};
  UTM_PARAMS.forEach(([key, field]) => {
    const value = params.get(key);
    if (value) marketing[field] = value;
  });

  const adPlatform = AD_PLATFORMS.find(([key]) => !!params.get(key));
  if (adPlatform) {
    const [key, platform] = adPlatform;
    marketing.adPlatform = platform;
    marketing.adClickId = params.get(key)!;
  }

  return marketing;
};

export const resolveAndPersistMetadataParams = (search?: string): MetadataParams => {
  const params = new URLSearchParams(search ?? document.location.search);
  const urlEntrypoint = params.get(QueryParamKeys.Entrypoint);
  const stored = readFromStorage();

  const merged: MetadataParams = resolveMarketingParams(params);
  const entrypoint = urlEntrypoint ?? stored.entrypoint;
  if (entrypoint) merged.entrypoint = entrypoint;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch (err) {
    console.warn('Failed to write metadata params to sessionStorage', err);
  }

  if (params.has(QueryParamKeys.Entrypoint)) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(QueryParamKeys.Entrypoint);
      window.history.replaceState({}, '', url.toString());
    } catch (err) {
      console.warn('Failed to strip entrypoint from URL', err);
    }
  }

  return merged;
};

// Read the params captured earlier in the session (resolveAndPersistMetadataParams runs when the
// ecomm machine builds its initial state, before any network call).
export const getPersistedMetadataParams = (): MetadataParams => readFromStorage();
